/**
 * Utility functions for phone number formatting and validation
 */

/**
 * Cleans and formats a phone number to international format (E.164)
 * Handles Ghana phone numbers (country code: 233)
 * 
 * Examples:
 * - "0557160977" → "233557160977"
 * - "233 557 160 977" → "233557160977"
 * - "+233557160977" → "233557160977"
 * - "557-160-977" → "233557160977"
 * 
 * @param phone - The phone number to clean
 * @param defaultCountryCode - Default country code if not present (default: "233" for Ghana)
 * @returns Cleaned phone number in international format or null if invalid
 */
export function cleanPhoneNumber(
  phone: string | null | undefined,
  defaultCountryCode: string = "233"
): string | null {
  if (!phone) return null;
  const raw = String(phone).trim();
  if (!raw) return null;

  // Remove all non-digit characters (spaces, dashes, parentheses, plus signs, etc.)
  let cleaned = raw.replace(/\D/g, "");

  // If empty after cleaning, return null
  if (!cleaned) return null;

  // Handle international prefix like 00 (e.g. 00233...)
  if (cleaned.startsWith("00")) {
    cleaned = cleaned.slice(2);
  }

  // If user (or UI) accidentally duplicated the country code, collapse it (e.g. 233233xxxxxxxxx -> 233xxxxxxxxx)
  // Do this only when it produces a plausible shorter number.
  const dupPrefix = `${defaultCountryCode}${defaultCountryCode}`;
  while (cleaned.startsWith(dupPrefix) && cleaned.length > defaultCountryCode.length + 8) {
    cleaned = cleaned.slice(defaultCountryCode.length);
  }

  // Common cleanup: if number is like <cc>0XXXXXXXXX, strip the trunk "0" after the country code.
  // (This happens when users type a local number like 0XX... while also selecting a country code.)
  const cc0Prefix = `${defaultCountryCode}0`;
  if (cleaned.startsWith(cc0Prefix) && cleaned.length > defaultCountryCode.length + 8) {
    cleaned = defaultCountryCode + cleaned.slice(cc0Prefix.length);
  }

  // If starts with country code (after cleanup), keep as is
  if (cleaned.startsWith(defaultCountryCode)) return cleaned;

  // If starts with 0, remove it and add country code
  // (For Ghana, this converts 024xxxxxxx -> 23324xxxxxxx)
  if (cleaned.startsWith("0") && cleaned.length === 10) {
    cleaned = cleaned.substring(1);
    return defaultCountryCode + cleaned;
  }

  // If it's 9 digits (local Ghana number without leading 0), add country code
  if (cleaned.length === 9) {
    return defaultCountryCode + cleaned;
  }

  // If it's 10 digits (e.g. US/UK local), add country code
  if (cleaned.length === 10) {
    return defaultCountryCode + cleaned;
  }

  // If it looks like it already includes a country code, keep as-is (avoid prefixing Ghana onto e.g. +1, +44, +234, etc.)
  if (cleaned.length >= 11) return cleaned;

  return null;
}

/**
 * Converts a phone number into E.164 format with a leading "+" (e.g. "+233557160977").
 * Returns null if the input cannot be normalized.
 */
export function phoneToE164(
  phone: string | null | undefined,
  defaultCountryCode: string = "233"
): string | null {
  const cleaned = cleanPhoneNumber(phone, defaultCountryCode);
  return cleaned ? `+${cleaned}` : null;
}

/**
 * Validates if a phone number is in the correct format for Ghana
 *
 * @param phone - The phone number to validate
 * @returns true if valid, false otherwise
 */
export function isValidGhanaPhone(phone: string | null | undefined): boolean {
  return isValidPhoneForCountry(phone, "233");
}

/**
 * Supported sign-up country codes (digits only, no +).
 * Used for validation and cleaning when user selects a country in the sign-up form.
 */
export const SUPPORTED_PHONE_COUNTRY_CODES = ["233", "234", "1", "44"] as const;

/**
 * Validates a phone number for a given country code (digits only, e.g. "233", "234", "1", "44").
 * Use after cleanPhoneNumber(phone, countryCode) so the number includes the country prefix.
 *
 * @param phone - Raw or cleaned phone number (will be cleaned with the given countryCode)
 * @param countryCode - Country code digits only, e.g. "233" (Ghana), "234" (Nigeria), "1" (US/Canada), "44" (UK)
 * @returns true if valid for that country
 */
export function isValidPhoneForCountry(
  phone: string | null | undefined,
  countryCode: string
): boolean {
  if (!phone) return false;
  const code = String(countryCode).replace(/\D/g, "") || "233";
  const cleaned = cleanPhoneNumber(phone, code);
  if (!cleaned || !cleaned.startsWith(code)) return false;

  const local = cleaned.slice(code.length);
  const localDigits = local.replace(/\D/g, "");

  switch (code) {
    case "233":
      // Ghana: 9 digits after 233; valid prefixes 20, 23, 24, 26, 27, 28, 50, 54, 55, 56, 57, 59
      return /^233(20|23|24|26|27|28|50|54|55|56|57|59)\d{7}$/.test(cleaned);
    case "234":
      // Nigeria: 10 digits after 234 (e.g. 8012345678, 7012345678)
      return cleaned.length === 13 && /^234[789]\d{9}$/.test(cleaned);
    case "1":
      // US/Canada: 10 digits after 1
      return cleaned.length === 11 && /^1\d{10}$/.test(cleaned);
    case "44":
      // UK: 10 or 11 digits after 44 (e.g. 7123456789, 71234567890)
      return cleaned.length >= 12 && cleaned.length <= 13 && /^44\d{10,11}$/.test(cleaned);
    default:
      // Unknown country: accept 9–12 digits after code (permissive)
      return localDigits.length >= 9 && localDigits.length <= 12;
  }
}

/**
 * Formats a phone number for display (with spaces)
 * 
 * @param phone - The phone number to format
 * @returns Formatted phone number for display (e.g., "233 55 716 0977")
 */
export function formatPhoneForDisplay(phone: string | null | undefined): string {
  if (!phone) return "";

  const cleaned = cleanPhoneNumber(phone);
  if (!cleaned) return phone || "";

  // Format as: 233 XX XXX XXXX
  if (cleaned.length === 12 && cleaned.startsWith("233")) {
    return `${cleaned.substring(0, 3)} ${cleaned.substring(3, 5)} ${cleaned.substring(5, 8)} ${cleaned.substring(8)}`;
  }

  return cleaned;
}

/**
 * Formats a phone number for local display (without country code)
 * 
 * @param phone - The phone number to format
 * @returns Formatted local phone number (e.g., "055 716 0977")
 */
export function formatPhoneLocal(phone: string | null | undefined): string {
  if (!phone) return "";

  const cleaned = cleanPhoneNumber(phone);
  if (!cleaned) return phone || "";

  // Remove country code and format as: 0XX XXX XXXX
  if (cleaned.length === 12 && cleaned.startsWith("233")) {
    const local = "0" + cleaned.substring(3);
    return `${local.substring(0, 4)} ${local.substring(4, 7)} ${local.substring(7)}`;
  }

  return cleaned;
}

